<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watchEffect } from 'vue';
import BaseButton from '@/components/base/BaseButton.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseSelect from '@/components/base/BaseSelect.vue';
import BaseCheckbox from '@/components/base/BaseCheckbox.vue';

import { useUserStore, getUniqueId, maskEmail } from '@/store/userStore';
import { getLabel } from '@/utils/data-display-utils';
import { useRouter } from 'vue-router';
import { paths } from '@/utils/paths';
import { userService } from '@/services/user.service';
import { authService } from '@/services/auth.service';
import { mask } from '@/utils/mask-util';
import { useCommunityStore } from '@/store/communityStore';

// LIFECYCLE
onMounted(() => {
  userStore.loadLanguages();
  userStore.loadGenders();
});

// STORE
const userStore = useUserStore();
const communityStore = useCommunityStore();

// CONST
const router = useRouter();
const community = computed(() => communityStore.community);

// REFS
const email = ref<string>(maskEmail(userStore.user.email));
const idIsHidden = ref<boolean>(true);
const isSaving = ref<boolean>(false);
const formStatusMessage = ref<string>('');
const formStatusTone = ref<'success' | 'error'>('success');
const formStatusRef = ref<HTMLElement | null>(null);

interface InitialInformation {
  first_name: string;
  last_name: string;
  language_code: string;
  gender: string;
  year_birth: string;
  postal_code: string;
  email: string;
  emailConsent: boolean;
  isCommunityMailingListMember: boolean;

  [key: string]: string | boolean;
}

function computeInitialInformation(): InitialInformation {
  return {
    first_name: userStore.user.firstName,
    last_name: userStore.user.lastName,
    language_code: userStore.user.preferredLanguage,
    gender: userStore.user.gender,
    year_birth: userStore.user.birthYear,
    postal_code: userStore.user.postalCode,
    email: userStore.user.email,
    emailConsent: userStore.user.emailConsent ? true : false,
    isCommunityMailingListMember: userStore.user.isCommunityMailingListMember ? true : false,
  };
}

const initialInformation = computed(computeInitialInformation);

const userInformation = ref({ ...initialInformation.value });

const isFormChanged = computed(() => {
  for (const key in initialInformation.value) {
    if (Object.prototype.hasOwnProperty.call(initialInformation.value, key)) {
      if (initialInformation.value[key] !== userInformation.value[key]) {
        return true;
      }
    }
  }
  return false;
});

// Watch for changes in userStore.user and update userInformation
watchEffect(() => {
  const updatedInitialInfo = computeInitialInformation();
  for (const key in updatedInitialInfo) {
    if (Object.prototype.hasOwnProperty.call(updatedInitialInfo, key)) {
      userInformation.value[key] = updatedInitialInfo[key];
    }
  }
});

const displayedUniqueId = computed(() => {
  if (!userStore.user?.uniqueId) {
    return '';
  }

  return idIsHidden.value ? getUniqueId(userStore.user) : userStore.user.uniqueId.value;
});

const userIdToggleLabel = computed(() => {
  return getLabel(
    idIsHidden.value ? 'profile.show-account-identifier' : 'profile.hide-account-identifier',
    community.value?.languageEnabled,
  );
});

const emailToggleLabel = computed(() => {
  return getLabel('profile.toggle-email-visibility', community.value?.languageEnabled);
});

// FUNCTIONS
function clearFormStatus() {
  formStatusMessage.value = '';
  formStatusTone.value = 'success';
}

function updateValue<K extends keyof InitialInformation>(key: K, newValue: any) {
  clearFormStatus();

  if (key === 'gender' || key === 'language_code') {
    userInformation.value[key] = newValue.value;
  } else if (key === 'year_birth' || key === 'first_name' || key === 'last_name' || key === 'postal_code') {
    userInformation.value[key] = newValue;
  } else {
    userInformation.value[key] = newValue;
  }
}

async function updateUser() {
  if (!isFormChanged.value || isSaving.value) {
    return;
  }

  clearFormStatus();
  isSaving.value = true;

  const response = await userService.updateUser(userInformation.value as any);
  const isSuccessful = response?.success === true;

  formStatusTone.value = isSuccessful ? 'success' : 'error';
  formStatusMessage.value = getLabel(
    isSuccessful ? 'profile.save-success' : 'profile.save-error',
    community.value?.languageEnabled,
  );
  isSaving.value = false;

  await nextTick();
  formStatusRef.value?.focus();
}

function getUserName() {
  return userStore.getUserName;
}

function displayedEmail(emailIsHidden: boolean): void {
  if (userStore.user && userStore.user.email) {
    email.value = emailIsHidden ? maskEmail(userStore.user.email) : userStore.user.email;
  }
}

function toggleEye() {
  idIsHidden.value = !idIsHidden.value;
}

function logout() {
  router.push(paths.root);
  authService.logout();
}

function getDefaultSelectedLanguage() {
  if (userInformation.value.language_code) {
    return userStore.languages.find((language) => language.value === userInformation.value.language_code);
  }

  return userStore.languages[0] ?? null;
}

function getGender() {
  const genderValue = Number(userInformation.value.gender);
  return userStore.genders.find((gender: any) => gender.value === genderValue) ?? null;
}
</script>

<template>
  <div class="my-profile">
    <div class="my-profile__container">
      <div class="my-profile__title">
        <h1>{{ getLabel('profile.my-account', community?.languageEnabled) }}</h1>
      </div>
      <div class="my-profile__information">
        <div class="my-profile__information--picture"></div>
        <div class="my-profile__information--user">
          <div class="my-profile__information--name" v-if="getUserName()">
            {{ getUserName() }}
          </div>
          <div class="my-profile__information--id">
            <span class="sr-only">{{ getLabel('profile.account-identifier', community?.languageEnabled) }}: </span>
            <span aria-live="polite">{{ displayedUniqueId }}</span>
            <button
              type="button"
              class="my-profile__information-toggle"
              :aria-label="userIdToggleLabel"
              :aria-pressed="!idIsHidden"
              @click="toggleEye()"
            >
              <q-icon :name="idIsHidden ? 'visibility_off' : 'visibility'" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div class="my-profile__information--action">
        <BaseButton
          isSmall
          isOrange
          class="primary"
          :text="getLabel('profile.logout', community?.languageEnabled)"
          @click="logout()"
        />
      </div>

      <q-separator />

      <q-form class="my-profile__form" @submit.prevent="updateUser" novalidate>
        <div class="my-profile__form--content">
          <div class="my-profile__form--info">
            <div class="my-profile__form--name">
              <div class="my-profile__form-field my-profile__form--firstname">
                <label id="first-name-label" for="first-name" class="my-profile__form--label">
                  {{ getLabel('profile.first-name', community?.languageEnabled) }}
                </label>
                <BaseInput
                  id="first-name"
                  class="my-profile__form--input"
                  :placeholder="getLabel('profile.first-name', community?.languageEnabled)"
                  :model="userInformation.first_name"
                  type="text"
                  name="first-name"
                  autocomplete="given-name"
                  aria-labelledby="first-name-label"
                  @update="updateValue('first_name', $event)"
                />
              </div>
              <div class="my-profile__form-field my-profile__form--lastname">
                <label id="last-name-label" for="last-name" class="my-profile__form--label">
                  {{ getLabel('profile.last-name', community?.languageEnabled) }}
                </label>
                <BaseInput
                  id="last-name"
                  class="my-profile__form--input"
                  :placeholder="getLabel('profile.last-name', community?.languageEnabled)"
                  :model="userInformation.last_name"
                  type="text"
                  name="last-name"
                  autocomplete="family-name"
                  aria-labelledby="last-name-label"
                  @update="updateValue('last_name', $event)"
                />
              </div>
            </div>
            <div class="my-profile__form-field">
              <label id="preferred-language-label" for="preferred-language" class="my-profile__form--label">
                {{ getLabel('profile.preferred-language', community?.languageEnabled) }}
              </label>
              <BaseSelect
                id="preferred-language"
                class="my-profile__form--input"
                :options="userStore.languages"
                :default-value="getDefaultSelectedLanguage()"
                name="preferred-language"
                aria-labelledby="preferred-language-label"
                @update="updateValue('language_code', $event)"
              />
            </div>
            <div class="my-profile__form-field">
              <label id="gender-label" for="gender" class="my-profile__form--label">
                {{ getLabel('profile.gender', community?.languageEnabled) }}
              </label>
              <BaseSelect
                id="gender"
                class="my-profile__form--input"
                :options="userStore.genders"
                :default-value="getGender()"
                name="gender"
                aria-labelledby="gender-label"
                @update="updateValue('gender', $event)"
              />
            </div>
            <div class="my-profile__form-field">
              <label id="year-label" for="year" class="my-profile__form--label">
                {{ getLabel('profile.birth-year', community?.languageEnabled) }}
              </label>
              <BaseInput
                id="year"
                class="my-profile__form--input"
                placeholder="AAAA"
                :mask="mask.year"
                :model="userInformation.year_birth"
                type="text"
                name="year"
                aria-labelledby="year-label"
                @update="updateValue('year_birth', $event)"
              />
            </div>
            <div class="my-profile__form-field">
              <label id="postal-code-label" for="postal-code" class="my-profile__form--label">
                {{ getLabel('profile.postal-code', community?.languageEnabled) }}
              </label>
              <BaseInput
                id="postal-code"
                class="my-profile__form--input"
                placeholder="A1A 1A1"
                :model="userInformation.postal_code"
                :mask="mask.postalCode"
                type="text"
                name="postal-code"
                autocomplete="postal-code"
                aria-labelledby="postal-code-label"
                @update="updateValue('postal_code', $event)"
              />
            </div>
          </div>

          <div class="my-profile__form--notification">
            <div class="my-profile__form--title">
              {{ getLabel('profile.email-notification', community?.languageEnabled) }}
            </div>
            <div class="my-profile__form-field">
              <label id="readonly-email-label" for="readonly-email" class="my-profile__form--label">
                {{ getLabel('profile.email', community?.languageEnabled) }}
              </label>
              <BaseInput
                id="readonly-email"
                class="my-profile__form--input"
                readonly
                hide
                :model="email"
                type="text"
                name="readonly-email"
                autocomplete="email"
                aria-labelledby="readonly-email-label"
                :hide-toggle-aria-label="emailToggleLabel"
                @update="updateValue('email', $event)"
                @toggleEye="displayedEmail"
              />
            </div>
            <div class="my-profile__form--checkboxes">
              <BaseCheckbox
                :model="userInformation.emailConsent"
                :label="getLabel('profile.email-permission', community?.languageEnabled)"
                type="checkbox"
                @update="updateValue('emailConsent', $event)"
              />
              <!--                      todo: need to create a new story for disable your mailing list-->
              <!--                      <BaseCheckbox-->
              <!--                            :model="userInformation.isCommunityMailingListMember"-->
              <!--                            :label="getLabel('profile.mailing-list-member', community?.languageEnabled)"-->
              <!--                            type="checkbox"-->
              <!--                            @update="updateValue('isCommunityMailingListMember', $event)"-->
              <!--                        />-->
            </div>
          </div>
        </div>

        <div class="my-profile__button--save">
          <BaseButton
            :disabled="!isFormChanged || isSaving"
            :is-loading="isSaving"
            :text="getLabel('profile.save-changes', community?.languageEnabled)"
            type="submit"
            primary
          />
          <p
            v-if="formStatusMessage"
            ref="formStatusRef"
            class="my-profile__form--status"
            :class="`my-profile__form--status-${formStatusTone}`"
            :role="formStatusTone === 'error' ? 'alert' : 'status'"
            :aria-live="formStatusTone === 'error' ? 'assertive' : 'polite'"
            tabindex="-1"
          >
            {{ formStatusMessage }}
          </p>
        </div>
      </q-form>
    </div>
  </div>
</template>

<style scoped lang="scss">
.my-profile {
  height: 100%;
  display: flex;

  &__container {
    display: flex;
    flex-direction: column;
    margin: $space-2xl auto;
    width: 1000px;
  }

  &__title {
    h1 {
      font-size: $large-title;
      font-weight: bold;
      padding: $space-2xl 0 $space-lg 0;
      line-height: 100%;
      margin: 0;
    }
  }

  &__button {
    &--save {
      padding: $space-md 0;
    }
  }

  &__information {
    display: flex;
    margin-bottom: $space-xl;
    align-items: center;

    &--picture {
      display: flex;
      flex-direction: column;
      align-items: center;

      .base-link {
        margin-top: $space-sm;
      }
    }

    &--picture-upload {
      margin-top: $space-sm;
      cursor: pointer;
      transition: all 300ms ease;
      text-decoration: none;
      border-bottom: 1px solid transparent;

      &:hover {
        color: $color-neutral-black;
        border-color: black;
        transition: all 300ms ease;
      }
    }

    &--action {
      display: flex;
      flex-direction: column;
      max-width: 160px;
      align-items: flex-start;
      margin-bottom: $space-lg;

      .base-button {
        margin-bottom: $space-sm;
      }
    }

    &--user {
      display: flex;
      flex-direction: column;
      margin-left: $space-xl;
    }

    &--name {
      font-size: $large-title;
    }

    &--id {
      display: flex;
      align-items: center;
      font-size: $small-title;
      font-weight: 400;
      color: $color-neutral-black;
    }
  }

  &__information-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: $space-sm;
    padding: 0;
    border: 0;
    background: transparent;
    color: $color-neutral-black;
    cursor: pointer;

    .q-icon {
      font-size: $large-title;
    }

    &:focus-visible {
      outline: none;
      border-radius: 9999px;
      box-shadow:
        0 0 0 2px $color-neutral-white,
        0 0 0 4px var(--q-primary) !important;
    }
  }

  &__form {
    margin-top: $space-xl;
    min-height: 300px;

    &--title {
      font-size: $large-title;
      margin-bottom: $space-md;
      color: $color-neutral-black;
    }

    &--content {
      display: flex;
    }

    .base-button {
      margin-top: $space-lg;
    }

    &-field {
      display: flex;
      flex-direction: column;
    }

    &--name {
      display: flex;
      width: 100%;
    }

    &--firstname {
      width: 100%;
      padding-right: $space-lg;
    }

    &--lastname {
      width: 100%;
    }

    &--input {
      padding: $space-sm 0 $space-lg 0;
    }

    &--label {
      color: $color-neutral-black;
      font-weight: 600;
    }

    &--checkboxes {
      display: flex;
      flex-direction: column;
    }

    &--notification {
      width: 100%;
      padding-left: $space-xl;
    }

    &--info {
      width: 100%;
      padding-right: $space-xl;
    }

    &--status {
      margin: $space-md 0 0;
      font-weight: 600;

      &:focus {
        outline: 2px solid var(--q-primary);
        outline-offset: 4px;
        border-radius: 4px;
      }

      &-success {
        color: $color-green;
      }

      &-error {
        color: $color-red;
      }
    }
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  &__button {
    &--save {
      padding: $space-md 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
  }
}

@media screen and (max-width: $breakpoint-xl) {
  .my-profile {
    &__container {
      width: 900px;
    }
  }
}

@media screen and (max-width: $breakpoint-lg) {
  .my-profile {
    &__container {
      width: 700px;
    }

    &__title {
      padding: 32px 0;
      margin: auto;
    }

    &__information {
      margin: auto;
      align-items: center;

      &--action {
        padding-top: 32px;
        max-width: 100%;
        margin: auto;
        align-items: center;
        justify-content: center;
      }
    }

    &__button {
      &--save {
        align-items: stretch;

        .base-button {
          width: 100%;
        }
      }
    }

    &__form {
      &--content {
        flex-wrap: wrap;
      }

      &--notification {
        padding-left: 0;
      }

      &--info {
        padding-right: 0;
      }
    }
  }
}

@media screen and (max-width: $breakpoint-md) {
  .my-profile {
    &__container {
      width: 500px;
    }

    &__form {
      &--name {
        display: inherit;
      }

      &--firstname {
        padding: 0;
      }
    }
  }
}

@media screen and (max-width: $breakpoint-sm) {
  .my-profile {
    &__container {
      width: 400px;
    }
  }
}
</style>
